import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { LocationSweepstakeDetailsHeader } from './LocationSweepstakeDetailsHeader'
import { useParams } from 'react-router-dom'
import { useGetSweepstakeById } from 'hooks/api/useGetSweepstakeById'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { isNil } from 'lodash'
import { usePutContest } from 'hooks/api/usePutContest'
import { formatFullDateTime } from 'utils/util'
import { useQueryClient } from '@tanstack/react-query'
import { isPresent } from '@jjvgaming/player-payback-library'
import { useState } from 'react'
import { PromoteSweepstakeModal } from 'pages/Contests/Sweeptakes/SweepstakesDetailsPage/PromoteSweepstakeModal'
import { SweepstakeEntryMultiplierSection } from 'pages/Contests/SweepstakeEntryMultiplier/SweepstakeEntryMultiplierSection'

export const LocationSweepstakeDetailsSettingsPage = () => {
  const { id: idParam } = useParams()
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState<boolean>(false)
  const qc = useQueryClient()
  const sweepstakeId = Number(idParam)
  const sweepstakeQuery = useGetSweepstakeById(Number(sweepstakeId))

  if (isNil(idParam)) {
    throw Error('Id Param Missing')
  }

  const { mutateAsync: putContestAsync } = usePutContest({
    onError: () => {},
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/sweepstakes'] })
      setIsPromoteModalOpen(false)
    },
  })

  if (sweepstakeQuery.isPending) {
    return <ActivityIndicator />
  }

  if (sweepstakeQuery.isError || isNil(sweepstakeQuery.data)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  const sweepstake = sweepstakeQuery.data
  const isContestPromoted = !isNil(sweepstake.sweepstakeData?.promoted)

  const isSetUpValid =
    isPresent(sweepstake.name) &&
    sweepstake.name !== '' &&
    !isNil(sweepstake.startDate) &&
    !isNil(sweepstake.endDate) &&
    !isNil(sweepstake.imageFileName) &&
    !isNil(sweepstake.publicImageUrl)

  const sweepstakeData = sweepstake.sweepstakeData

  if (!sweepstakeData) {
    return (
      <LocationSweepstakeDetailsHeader
        sweepstake={sweepstake}
        currentTab={`/Contests/Sweepstakes/${sweepstake.id}/Settings`}
      >
        <Stack>
          <Typography variant="h4">
            We had an issue with the sweepstake data.
          </Typography>
        </Stack>
      </LocationSweepstakeDetailsHeader>
    )
  }

  return (
    <LocationSweepstakeDetailsHeader
      sweepstake={sweepstake}
      currentTab={`/Contests/LocationSweepstakes/${sweepstake.id}/Settings`}
    >
      <Stack gap={'40px'}>
        <PromoteSweepstakeModal
          sweepstakeName={sweepstake.name}
          isModalOpen={isPromoteModalOpen}
          toggleIsOpen={() => setIsPromoteModalOpen((x) => !x)}
          onConfirm={async () => {
            await putContestAsync({
              ...sweepstake,
              sweepstakeData: {
                ...sweepstakeData,
                contestId: Number(sweepstakeData?.contestId),
                promoted: new Date().toUTCString(),
              },
            })
          }}
        />
        <Box>
          <Typography variant="h3" pt={'8px'} pb={'8px'}>
            Promote to Patrons
          </Typography>
          <Stack
            spacing={'24px'}
            sx={{
              p: '24px',
              width: '100%',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
            }}
          >
            <Stack
              direction={'row'}
              spacing={'24px'}
              justifyContent={'space-between'}
            >
              <Typography variant="label-form" alignContent={'center'}>
                Once &quot;Promote&quot; is selected, all patrons will see this
                sweepstakes as upcoming in the J&J Gaming App. Once the
                sweepstakes goes live, this promotion will be replaced by the
                live sweepstakes.{' '}
              </Typography>
              <Button
                variant="contained"
                onClick={async () => {
                  setIsPromoteModalOpen(true)
                }}
                disabled={isContestPromoted || !isSetUpValid}
              >
                {isContestPromoted ? 'Promoted' : 'Promote'}
              </Button>
            </Stack>
            {!isSetUpValid && !isContestPromoted ? (
              <Alert severity="error" className="p-6">
                Unable to promote sweepstakes. All fields must be completed to
                successfully promote sweepstakes in the J&J Gaming app. Make
                sure an image and appropriate start date is provided.
              </Alert>
            ) : null}
            {isContestPromoted && (
              <Typography
                variant="label-form"
                className="pt-4"
                color={'text.secondary'}
              >
                Promoted:{' '}
                {formatFullDateTime(sweepstake.sweepstakeData?.promoted)}
              </Typography>
            )}
          </Stack>
        </Box>

        <SweepstakeEntryMultiplierSection sweepstake={sweepstake} />
      </Stack>
    </LocationSweepstakeDetailsHeader>
  )
}
